<template>
  <div class="header-container">
    <h1>{{ $t('project.documents.DocumentsHeader.documents') }}</h1>

    <p class="my-3 text-accent-lighten-2">
      {{ $t('project.documents.DocumentsHeader.storeAndExchangeDocuments') }}
      <AppInfoTooltip>
        <p>{{ $t('project.documents.DocumentsHeader.documentsPageInfo1') }}</p>
        <p>{{ $t('project.documents.DocumentsHeader.documentsPageInfo2') }}</p>
      </AppInfoTooltip>
    </p>

    <div class="sticky-container">
      <v-row>
        <!--SEARCH-->
        <v-col cols="12" md="auto" lg="6">
          <v-row class="align-center">
            <v-col cols="auto">
              <SearchTextField v-model="search"
                               :tooltipText="$t('project.documents.DocumentsHeader.searchPlaceholder')"
                               data-locator="documents-search-input"
              />
            </v-col>
            <v-col cols="auto">
              <app-button type="outlined" variant="neutral" data-locator="filters-button" @click="onOpenFiltersPane">
                <template #left-icon>
                  <app-icon-ds
                    icon="sliders"
                    weight="far"
                    size="xs"
                  />
                </template>
                {{ $t('common.filters') }}
              </app-button>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12" md="auto" lg="6">
          <v-row class="justify-end align-center">
            <v-col v-if="currentRoom.isPM && !hasTodoFolderOrChild" md="auto">
              <AppTooltip v-if="currentRoom.isPM && !hasTodoFolderOrChild">
                <template #activator="{ props }">
                  <div v-bind="props">
                    <v-switch
                      v-model="numberingEnabled"
                      readonly
                      class="mt-0"
                      density="compact"
                      :loading="patchNumberingPending"
                      hide-details
                      data-locator="numbering-toggle"
                      :data-toggle-state="numberingEnabled? 'enabled' : 'disabled'"
                      @click="!patchNumberingPending && onNumberingChange()"
                    >
                      <template #label>
                        <span>{{ $t("project.documents.DocumentsHeader.numbering") }}</span>
                      </template>
                    </v-switch>
                  </div>
                </template>
                <span>{{ $t("project.documents.DocumentsHeader.numberingTooltip") }}</span>
              </AppTooltip>
            </v-col>
            <v-col md="auto">
              <v-menu>
                <template #activator="{ props }">
                  <app-button type="outlined" variant="neutral" v-bind="props" data-locator="options-menu-button">
                    {{ $t("project.documents.DocumentsHeader.options") }}
                    <template #right-icon>
                      <app-icon-ds icon="chevron-down" size="xs"/>
                    </template>
                  </app-button>
                </template>
                <v-list data-locator="documents-options-menu">
                  <AppDownloadFile :link="exportIndexToExcelLink" class="d-block">
                    <template #default="{ submitForm }">
                      <v-list-item @click="submitForm">
                        <v-list-item-title class="d-flex align-center" data-locator="excel-index-export-menu-item">
                          <app-icon-ds
                            icon="file-excel"
                            weight="far"
                            size="sm"
                            class="mr-2 ml-1"
                          />
                          {{ $t("project.documents.DocumentsHeader.exportIndexToExcel") }}
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                  </AppDownloadFile>
                  <AppDownloadFile :link="exportIndexToPdfLink" forceReader class="d-block">
                    <template #default="{ submitForm }">
                      <v-list-item @click="submitForm">
                        <v-list-item-title class="d-flex align-center" data-locator="pdf-index-export-menu-item">
                          <app-icon-ds
                            icon="file-pdf"
                            weight="far"
                            size="sm"
                            class="mr-2 ml-1"
                          />
                          {{ $t("project.documents.DocumentsHeader.exportIndexToPdf") }}
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                  </AppDownloadFile>
                  <v-list-item v-if="currentRoom.isPM && !hasTodoFolderOrChild"
                               @click="existingTreeDialogIsOpen = true">
                    <v-list-item-title class="d-flex align-center" data-locator="folder-structure-import-menu-item">
                      <app-icon-ds
                        icon="file-excel"
                        weight="far"
                        size="sm"
                        class="mr-2 ml-1"
                      />
                      {{ $t("project.documents.DocumentsHeader.dropdownIcon.existingTree") }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="onSendDocumentsIndexByEmail">
                    <v-list-item-title class="d-flex align-center" data-locator="share-index-by-mail-menu-item">
                      <app-icon-ds
                        icon="envelope"
                        weight="far"
                        size="sm"
                        class="mr-2 ml-1"
                      />
                      {{ $t("project.documents.DocumentsHeader.sendIndexByEmail") }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
            <v-col v-if="isDataRoom && currentRoom.isPM" md="auto">
              <app-button
                type="outlined"
                data-locator="privary-and-security-button"
                :to="{
                  name: ROOM_SETTINGS_ROUTE_NAME,
                  params: {
                    roomType: computedRoomRouteType,
                  },
                  state: {
                    goTo: '#accessRightsContainer',
                  },
                }"
              >
                {{ $t('project.documents.DocumentsHeader.privacyAndSecurity') }}
              </app-button>
            </v-col>
            <v-col v-if="displayAddMenu" md="auto">
              <AppTooltip v-if="currentUserRights.canUpload && hasTodoFolderOrChild"
                          :disabled="!showSizeExceededTooltip">
                <template #activator="{ props }">
                  <!-- We bind the tooltip to the div, because it should be seen even with a disabled activator -->
                  <!-- This makes it able to see the tooltip when disabled-->
                  <div v-bind="props">
                    <app-button :disabled="showSizeExceededTooltip" @click="OPEN_DOCUMENTS_UPLOAD_FILES">
                      <template #left-icon>
                        <app-icon-ds icon="plus" weight="far" size="sm"/>
                      </template>
                      {{ $t("project.documents.DocumentsHeader.addFile") }}
                    </app-button>
                  </div>
                </template>
                {{ $t('project.documents.DocumentsHeader.sizeExceededTooltip') }}
              </AppTooltip>
              <v-menu v-else :disabled="showSizeExceededTooltip" open-on-hover data-locator="documents-add-menu">
                <template v-slot:activator="{ props }">
                  <!-- We bind the tooltip to the div, because it should be seen even with a disabled activator -->
                  <!-- This makes it able to see the tooltip when disabled, but not the menu -->
                  <!-- here use v-tooltip inside the v-menu because bug when use AppTooltip on v-menu -->
                  <AppTooltip :disabled="!showSizeExceededTooltip">
                    <template #activator="{ props: tooltipProps }">
                      <div v-bind="tooltipProps">
                        <app-button :disabled="showSizeExceededTooltip" v-bind="props" data-locator="add-menu-button">
                          <template #left-icon>
                            <app-icon-ds icon="plus" size="xs"/>
                          </template>
                          {{ $t("common.add") }}
                          <template #right-icon>
                            <app-icon-ds icon="chevron-down" size="xs"/>
                          </template>
                        </app-button>
                      </div>
                    </template>
                    {{ $t('project.documents.DocumentsHeader.sizeExceededTooltip') }}
                  </AppTooltip>
                </template>
                <v-list>
                  <v-list-item
                    v-if="currentUserRights.canCreateFolders && !hasTodoFolderOrChild"
                    data-locator="add-file-or-folder-menu-item"
                    @click="OPEN_DOCUMENTS_UPLOAD_FILES"
                  >
                    <v-list-item-title>
                      {{ $t("project.documents.DocumentsHeader.dropdownIcon.addFoldersOrFiles") }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-else-if="currentUserRights.canUpload && !isCurrentlyInTodoFolderRoot"
                    data-locator="add-file-menu-item"
                    @click="OPEN_DOCUMENTS_UPLOAD_FILES"
                  >
                    <v-list-item-title>
                      {{ $t("project.documents.DocumentsHeader.dropdownIcon.addFiles") }}
                    </v-list-item-title>
                  </v-list-item>
                  <PolyOfficeListItemImport
                    v-if="isImportFromPolyMenuDisplayed"
                    @polyOfficeOpenDialog="polyOfficeOpenDialogHandler"
                  />
                  <v-list-item
                    v-if="(currentRoom.isPM || currentUserRights.canCreateFolders) && !hasTodoFolderOrChild"
                    data-locator="new-folder-menu-item"
                    @click="OPEN_DOCUMENTS_CREATE_NEW_FOLDER"
                  >
                    <v-list-item-title>
                      {{ $t("project.documents.DocumentsHeader.dropdownIcon.newFolder") }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row v-show="breadcrumbIsShown">
        <v-col>
          <DocumentsBreadcrumb class="mx-6" />
        </v-col>
      </v-row>
      <v-row v-if="selectedDocuments.length > 0 && !loading" class="sticky-box">
        <v-col>
          <DocumentsActionBar class="mb-3"/>
        </v-col>
      </v-row>
    </div>

    <DocumentsHeaderFilters v-if="filtersCount > 0"/>

    <DocumentsExistingTreeDialog v-model:is-open="existingTreeDialogIsOpen" :mnemo="mnemo"/>
    <DocumentsNumberingDisableDialog
      v-if="showNumberingDisableDialog"
      :mnemo="mnemo"
      @confirm="confirmNumberingDisableDialog"
      @close="showNumberingDisableDialog = false"
    />

    <PolyOfficeDialog v-if="polyOfficeEnabled && dialogIsOpen" @callback="polyOfficeDialogCallback"/>
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { DocumentsImport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeDialog, { POLY_OFFICE_DIALOG_STATUS_SUCCESS } from '@/common/polyOffice/PolyOfficeDialog.vue'
import PolyOfficeListItemImport from '@/common/polyOffice/PolyOfficeListItemImport.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { getRoomRouteType } from '@/common/utils/rooms'
import { toast } from '@/common/utils/toast'
import DocumentsExistingTreeDialog from '@/project/documents/dialogs/DocumentsExistingTreeDialog.vue'
import DocumentsHeaderFilters from '@/project/documents/DocumentsHeaderFilters.vue'
import { ROOM_SETTINGS_ROUTE_NAME } from '@/router'
import {
  DELETE_SEARCH_RESULTS,
  GET_NUMBERING,
  GET_PAGINATED_DOCUMENTS,
  GET_SEARCH_RESULTS,
  LOAD_FOLDERS_TREE,
  OPEN_SHARE_DOCUMENTS_INDEX_BY_EMAIL_PANE,
  PATCH_NUMBERING,
} from '@/store/modules/documents/action_types'
import {
  RESET_DOCUMENTS_PAGINATION,
  SET_FILTERS_PANE_IS_OPEN,
  SET_NUMBERING_SNACKBAR_IS_OPEN,
  SET_SEARCH_QUERY,
} from '@/store/modules/documents/mutation_types'
import { DocumentsCreateNewFolderActionTypes } from '@/store/modules/documents-create-new-folder/action_types'
import { OPEN_DOCUMENTS_UPLOAD_FILES } from '@/store/modules/documents-upload-files/action_types'
import { GET_IMPORT_IFRAME } from '@/store/modules/poly-office/action_types'

import DocumentsNumberingDisableDialog from './dialogs/DocumentsNumberingDisableDialog.vue'
import DocumentsActionBar from './DocumentsActionBar.vue'
import DocumentsBreadcrumb from './DocumentsBreadcrumb.vue'

export default {
  name: 'DocumentsHeader',
  components: {
    AppTooltip,
    AppDownloadFile,
    AppInfoTooltip,
    DocumentsBreadcrumb,
    DocumentsExistingTreeDialog,
    DocumentsHeaderFilters,
    DocumentsActionBar,
    DocumentsNumberingDisableDialog,
    PolyOfficeDialog,
    PolyOfficeListItemImport,
    SearchTextField,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      ROOM_SETTINGS_ROUTE_NAME,
      searchQuery: '',
      showNumberingDisableDialog: false,
      existingTreeDialogIsOpen: false,
    }
  },
  computed: {
    ...mapState('documents', ['selectedDocuments', 'loading', 'paginatedDocumentsFilters', 'numberingEnabled', 'searchModeEnabled', 'patchNumberingPending', 'paginatedDocumentsCurrentFolderId']),
    ...mapState('documentsBreadcrumb', ['breadcrumb']),
    ...mapState('room', ['currentRoom']),
    ...mapState('polyOffice', ['dialogIsOpen', 'dialogSuccess']),
    ...mapGetters('room', ['currentUserRights', 'isDataRoom', 'isStorageFull']),
    ...mapGetters('documentsBreadcrumb', ['hasTodoFolderOrChild', 'isCurrentlyInTodoFolderRoot']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    computedRoomRouteType () {
      return getRoomRouteType(this.currentRoom)
    },
    exportIndexToExcelLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/documents-index-excel`
    },
    exportIndexToPdfLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/documents-index-pdf`
    },
    search: {
      get () {
        return this.searchQuery
      },
      set: debounce(function (val) {
        this.searchQuery = val
        this.searchDocs()
      }, 1000),
    },
    displayAddMenu () {
      return (
        this.paginatedDocumentsCurrentFolderId !== 'recentFolder' &&
        !this.isCurrentlyInTodoFolderRoot &&
        (
          this.currentRoom.isPM ||
          this.currentUserRights.canUpload ||
          this.currentUserRights.canCreateFolders
        )
      )
    },
    breadcrumbIsShown () {
      return this.breadcrumb.length > 1 && !this.searchModeEnabled
    },
    filtersCount () {
      return this.paginatedDocumentsFilters.length
    },
    isImportFromPolyMenuDisplayed () {
      return this.polyOfficeEnabled &&
        ((this.currentRoom.isPM && !this.hasTodoFolderOrChild) ||
          (this.currentUserRights.canUpload && !this.isCurrentlyInTodoFolderRoot))
    },
    showSizeExceededTooltip () {
      return this.isStorageFull
    },
  },
  watch: {
    searchModeEnabled (searchModeIsEnabled) {
      if (!searchModeIsEnabled) {
        this.searchQuery = ''
      }
    },
  },
  mounted () {
    this.GET_NUMBERING(this.mnemo)
  },
  methods: {
    ...mapActions('documentsCreateNewFolder', [DocumentsCreateNewFolderActionTypes.OPEN_DOCUMENTS_CREATE_NEW_FOLDER]),
    ...mapActions('documentsUploadFiles', [OPEN_DOCUMENTS_UPLOAD_FILES]),
    ...mapActions('documents', [
      DELETE_SEARCH_RESULTS,
      GET_NUMBERING,
      GET_SEARCH_RESULTS,
      LOAD_FOLDERS_TREE,
      OPEN_SHARE_DOCUMENTS_INDEX_BY_EMAIL_PANE,
      PATCH_NUMBERING,
      GET_PAGINATED_DOCUMENTS,
    ]),
    ...mapActions('polyOffice', [
      GET_IMPORT_IFRAME,
    ]),
    ...mapMutations('documents', [SET_FILTERS_PANE_IS_OPEN, SET_NUMBERING_SNACKBAR_IS_OPEN, SET_SEARCH_QUERY, RESET_DOCUMENTS_PAGINATION]),
    searchDocs () {
      if (this.search) {
        this.SET_SEARCH_QUERY(this.searchQuery)
      } else {
        this.DELETE_SEARCH_RESULTS(null)
      }

      this.RESET_DOCUMENTS_PAGINATION()
      this.GET_PAGINATED_DOCUMENTS()
    },
    async toggleNumbering () {
      try {
        await this.PATCH_NUMBERING({ mnemo: this.mnemo, value: !this.numberingEnabled })
        await this.GET_NUMBERING(this.mnemo)

        this.LOAD_FOLDERS_TREE()
        await this.GET_PAGINATED_DOCUMENTS()

        if (this.numberingEnabled) {
          this.SET_NUMBERING_SNACKBAR_IS_OPEN(true)
        } else {
          this.SET_NUMBERING_SNACKBAR_IS_OPEN(false)
          toast.success(this.$t('project.documents.DocumentsHeader.successNumberingDisabled'))
        }
      } catch {
        toast.error(this.$t('project.documents.DocumentsHeader.errorNumbering'))
      }
    },
    confirmNumberingDisableDialog () {
      this.showNumberingDisableDialog = false
      this.toggleNumbering()
    },
    onNumberingChange () {
      if (this.numberingEnabled) {
        this.showNumberingDisableDialog = true
      } else {
        this.toggleNumbering()
      }
    },
    onOpenFiltersPane () {
      this.SET_FILTERS_PANE_IS_OPEN(true)
    },
    onSendDocumentsIndexByEmail () {
      this.OPEN_SHARE_DOCUMENTS_INDEX_BY_EMAIL_PANE()
    },
    async refreshDocuments () {
      try {
        await this.GET_PAGINATED_DOCUMENTS()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.DocumentsHeader.getDocumentsError'))
      }
    },
    async polyOfficeOpenDialogHandler () {
      const payload = new DocumentsImport(
        this.mnemo,
        this.paginatedDocumentsCurrentFolderId,
      )
      await this.GET_IMPORT_IFRAME({
        payload,
        successMessage: this.$t('project.documents.DocumentsHeader.polyOffice.importSuccessful'),
      })
    },
    polyOfficeDialogCallback (data) {
      if (data.status === POLY_OFFICE_DIALOG_STATUS_SUCCESS) {
        this.refreshDocuments()
        toast.success(this.dialogSuccess)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.header-container {
  display: unset;
}
.sticky-container {
  position: sticky;
  top: 0;
  z-index: 2;
}
.sticky-container .v-row {
  background-color: rgb(var(--v-theme-originalGrey-lighten-4));
}
:deep(.v-switch .v-input__control) {
  grid-area: auto;
}
</style>
